"use client";

import { cn, useMediaQuery } from "@tosize/ui";
import { Children, Fragment, type ReactNode, useState } from "react";
import { useMedia } from "~/components/media/Provider";
import { MediaItem } from "./Item";

export function MediaDiyGrid({ children }: { children?: ReactNode }) {
	const { sections, onMediaChange } = useMedia();
	const isSmallDevice = useMediaQuery({ max: "lg" });

	const sectionsWithInfo = sections.map((section) => ({
		...section,
		items: section.items.map((item, index) => ({ ...item, position: index, id: section.id })),
	}));

	const total = isSmallDevice ? 3 : 5;
	const childrenCount = Children.count(children);
	const videoCount = sectionsWithInfo.find((section) => section.id === "videos")?.items.length ?? 0;
	const count = total - childrenCount - videoCount;

	const items = sectionsWithInfo
		.flatMap((section) => {
			return section.id === "images" ? section.items.filter((_, i) => i < count) : section.items;
		})
		.splice(0, total - childrenCount);

	const sliderItems = sectionsWithInfo
		.filter((s) => s.id !== "videos")
		.flatMap((section) => {
			return section.id === "images"
				? section.items.filter((_item, i) => i >= count || i === 0)
				: section.items;
		});

	const [activeIndex, setActiveIndex] = useState(0);

	function onItemClick(item: (typeof sectionsWithInfo)[number]["items"][number]) {
		const index =
			item.type === "youtube"
				? item.position - sectionsWithInfo.filter((section) => section.id === "images").length - 1
				: item.position;

		onMediaChange({ type: "detail", id: item.id, index });
	}

	return (
		<div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
			{items.map((item, index) => (
				<Fragment key={index}>
					{index === 0 ? (
						<div className="relative col-span-2 row-span-2 aspect-video h-full w-full overflow-hidden rounded-xl sm:rounded-r-none">
							<div
								className="scrollbar-none flex h-full w-full snap-x snap-mandatory flex-nowrap gap-4 overflow-x-auto overflow-y-hidden"
								onScroll={(event) => {
									const width = event.currentTarget.firstElementChild?.clientWidth ?? 1024;
									const left = event.currentTarget.scrollLeft;
									const index = Math.round(left / width);

									setActiveIndex(index);
								}}
							>
								{sectionsWithInfo
									.filter((s) => s.id !== "videos")
									.map((section) =>
										section.items.map((sliderItem, sliderIndex) => (
											<MediaItem
												item={{
													...sliderItem,
													priority: sliderIndex === 0,
												}}
												onClick={() => onItemClick(sliderItem)}
												className={cn("aspect-video w-full shrink-0 snap-start", {
													hidden:
														section.id === "images" && sliderIndex !== 0 && sliderIndex < count,
												})}
												key={sliderIndex}
											/>
										)),
									)}
							</div>

							{sliderItems.length > 1 && (
								<div className="absolute right-0 bottom-2 left-0 flex flex-row items-center justify-center gap-1">
									{Array.from({ length: sliderItems.length }).map((_, i) => (
										<span
											key={i}
											className={cn("overflow-hidden rounded-full motion-safe:transition", {
												"bg-shades": i !== activeIndex,
												hidden: i < activeIndex - 3 || i > activeIndex + 3,
												"h-[4px] w-[4px]": i === activeIndex - 3 || i === activeIndex + 3,
												"h-[6px] w-[6px]": i === activeIndex - 2 || i === activeIndex + 2,
												"h-[8px] w-[8px]": i === activeIndex - 1 || i === activeIndex + 1,
												"h-[10px] w-[10px] bg-primary-medium": i === activeIndex,
											})}
										/>
									))}
								</div>
							)}
						</div>
					) : (
						<MediaItem
							item={item}
							onClick={() => onItemClick(item)}
							className={cn({
								"col-span-2 row-span-2 rounded-xl sm:rounded-r-none": index === 0,
								"rounded-xl sm:rounded-l-none sm:rounded-br-none lg:rounded-none": index === 1,
								"rounded-xl sm:rounded-l-none sm:rounded-tr-none lg:rounded-tr-xl lg:rounded-br-none":
									index === 2,
								"lg:rounded-br-xl": index === 4,
								"[@media(max-width:1023px)]:hidden": index > 3 - childrenCount - 1,
							})}
						/>
					)}
				</Fragment>
			))}

			{children}
		</div>
	);
}
